import React, { useEffect } from 'react';
import Cookies from 'universal-cookie';
import HTMLParser from 'html-react-parser';
import { Link } from 'gatsby';
import clsx from 'clsx';

import { constructLink, formColor } from '../../helper';
import fontSizeSwitch from '../../helpers/fontSizeSwitch';
import getSnippets from '../../helpers/getSnippets';
import getTag from '../../helpers/getTag';
import Image from '../Image';

import * as styles from './styles.module.css';

const cookies = new Cookies();

const CookieWarning = ({
  themeData,
  cookie,
  pagePathList,
  articlePathList,
  categoryPathList,
  buttons,
  images,
  trackers,
  id,
  onCookieClick,
  show,
  sectionSlugs,
}) => {
  let linkButton;
  let declineButton;
  let acceptButton;
  if (
    cookie?.cookieLink?.active &&
    cookie?.cookieLink?.format === 'BUTTON' &&
    cookie?.cookieLink?.formatData?.styles?.id
  ) {
    linkButton = buttons?.find((b) => b.id === cookie.cookieLink.formatData.styles.id);
  }

  if (cookie?.cookieDecline?.id) declineButton = buttons?.find((b) => b.id === cookie.cookieDecline.id);
  if (cookie?.cookieButton?.id) acceptButton = buttons?.find((b) => b.id === cookie.cookieButton.id);

  useEffect(() => {
    if (show) {
      const wrapper = document.getElementById('CookieWarningWrapper');
      if (wrapper) wrapper.style.display = 'block';
    }
  }, [show]);

  const handleClick = (value) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    cookies.set('cookiesAccepted', value, { path: '/', expires: d });
    const wrapper = document.getElementById('CookieWarningWrapper');
    if (value) {
      const { head, before, start } = getSnippets(trackers, id);
      head.forEach((b) => {
        const tag = getTag(b, b.nodeName?.toLowerCase());
        document.head.appendChild(tag);
      });

      before.forEach((b) => {
        const tag = getTag(b, b.nodeName?.toLowerCase());
        document.body.appendChild(tag);
      });

      start.forEach((b) => {
        const tag = getTag(b, b.nodeName?.toLowerCase());
        document.body.prepend(tag);
      });
    }
    if (wrapper) wrapper.style.display = 'none';
    if (show) onCookieClick();
  };

  let titleColor;
  let textColor;
  let bgColor;
  let btnBgColor;
  let btnBorderColor;
  let btnColor;
  let linkColor;

  if (cookie.colour === 'light') {
    titleColor = '#000';
    textColor = '#000';
    [, bgColor] = themeData?.colors;
    [btnBgColor] = themeData?.colors;
    [btnBorderColor] = themeData?.colors;
    [, btnColor] = themeData?.colors;
    [, , , linkColor] = themeData?.colors;
  } else if (cookie.colour === 'dark') {
    titleColor = '#FFF';
    textColor = '#FFF';
    [, , , bgColor] = themeData?.colors;
    [, , , btnBgColor] = themeData?.colors;
    [btnBorderColor] = themeData?.colors;
    btnColor = '#fff';
    [, linkColor] = themeData?.colors;
  } else {
    bgColor = formColor(
      { solid: cookie?.bgColor },
      false,
      cookie?.bgOpacity ?? 1,
      undefined,
      themeData?.colors,
    ).backgroundColor;
    textColor = formColor(
      { solid: cookie?.textColor },
      false,
      cookie?.textOpacity ?? 1,
      undefined,
      themeData?.colors,
    )?.backgroundColor;
    titleColor = textColor;
    linkColor = formColor(
      { solid: cookie?.linkColor },
      false,
      cookie?.linkOpacity ?? 1,
      undefined,
      themeData?.colors,
    )?.backgroundColor;
  }

  let link;
  if (cookie?.cookieLink?.active) {
    ({ link } = constructLink(cookie.cookieLink, pagePathList, articlePathList, null, categoryPathList, sectionSlugs));
  }

  const getBtnStyle = (buttonStyle) => {
    let borderRadius;
    let boxShadow;

    let bColorHex = themeData?.colors[2];
    let bThickness = 0;

    if (buttonStyle?.border?.active) {
      if (buttonStyle.border.color !== '') bColorHex = buttonStyle.border.color;
      bThickness = `${buttonStyle.border.thickness}px`;
      borderRadius = `${buttonStyle.border.radius}px`;
    }

    const bColor = formColor(
      { solid: bColorHex },
      false,
      buttonStyle.border?.opacity >= 0 ? buttonStyle.border.opacity : 1,
      undefined,
      themeData?.colors,
    );

    const border = `${bColor.backgroundColor} solid ${bThickness}`;

    if (buttonStyle.shadow?.active) {
      const { x, y, blur, spread } = buttonStyle.shadow;

      let shadow = buttonStyle.shadow.color;
      if (buttonStyle.shadow.color === '') [, , shadow] = themeData?.colors;

      const sColor = formColor({ solid: shadow }, false, buttonStyle.shadow.opacity, undefined, themeData?.colors);

      boxShadow = `${x}px ${y}px ${blur}px ${spread}px ${sColor.backgroundColor}`;
    }

    const font = buttonStyle.fontColor?.color ?? '#FFF';
    const fontColor = formColor(
      { solid: font },
      undefined,
      buttonStyle.fontColor?.opacity ?? 1,
      undefined,
      themeData?.colors,
    );

    const backgroundColor = { solid: buttonStyle.backgroundColor?.solid || themeData?.colors[0] };
    const buttonBgColor = formColor(
      backgroundColor,
      false,
      buttonStyle.backgroundColor?.opacity ?? 1,
      undefined,
      themeData?.colors,
    );

    const hoverBgColor = formColor(
      { solid: buttonStyle.hoverBackgroundColor?.solid ?? 'COLOR_0' },
      false,
      buttonStyle.hoverBackgroundColor?.opacity ?? 1,
      undefined,
      themeData.colors,
    );

    const hoverFontColor = formColor(
      { solid: buttonStyle.hoverFontColor?.color ?? '#FFF' },
      undefined,
      buttonStyle.hoverFontColor?.opacity ?? 1,
      undefined,
      themeData.colors,
    );

    let fontFamily;
    let fontSize;
    let textTransform;
    let fontWeight;
    if (themeData) {
      fontFamily = themeData?.typography?.button?.name;
      fontSize = fontSizeSwitch(
        (buttonStyle.fontActive && buttonStyle.fontSize) || themeData?.typography?.button?.fontSize,
      );
      textTransform =
        (buttonStyle.fontActive && buttonStyle.textTransform) || themeData?.typography?.button?.textTransform;
      fontWeight = (buttonStyle.fontActive && buttonStyle.fontWeight) || themeData?.typography?.button?.weight;
    }

    return {
      borderRadius,
      border,
      boxShadow,
      fontFamily,
      fontSize,
      fontWeight,
      textTransform,
      color: fontColor?.backgroundColor,
      backgroundColor: buttonBgColor?.backgroundColor,
      padding: buttonStyle.padding?.active
        ? ['top', 'right', 'bottom', 'left'].map((prop) => `${buttonStyle.padding[prop]}px`).join(' ')
        : null,
      '--hoverFontColor': hoverFontColor?.backgroundColor,
      '--hoverBackgroundColor': hoverBgColor?.backgroundColor,
    };
  };

  const getBtnIcon = (icon) => {
    let color = icon.color || '#000000';
    color = formColor({ solid: color }, false, 1, undefined, themeData?.colors).backgroundColor;
    return (
      <i
        className={`entypo ${icon?.class}`}
        aria-hidden
        style={{
          fontSize: icon.size,
          color,
          marginRight: '4px',
        }}
      />
    );
  };

  const declineButtonStyle = declineButton && getBtnStyle(declineButton);
  const acceptButtonStyle = acceptButton && getBtnStyle(acceptButton);

  let acceptIcon;
  let declineIcon;
  if (cookie?.cookieButton?.icon?.active && cookie?.cookieButton?.icon?.class) {
    acceptIcon = getBtnIcon(cookie.cookieButton.icon);
  }
  if (cookie?.cookieDecline?.icon?.active && cookie?.cookieDecline?.icon?.class) {
    declineIcon = getBtnIcon(cookie.cookieDecline.icon);
  }

  let finishedLink;
  const { cookieLink } = cookie;
  if (cookieLink?.active && cookieLink?.format && cookieLink?.type) {
    let linkContent;
    let linkButtonStyle;
    switch (cookieLink.format) {
      case 'BUTTON':
        linkButtonStyle = linkButton && getBtnStyle(linkButton);
        linkContent = (
          <button
            className={clsx(
              styles.btn,
              linkButton?.hover?.active && linkButton?.hover?.zoom && styles.zoom,
              linkButton?.hover?.active && styles.hoverStyle,
            )}
            style={linkButtonStyle}
          >
            {HTMLParser(cookieLink.name)}
          </button>
        );
        break;
      case 'ICON': {
        let color = cookieLink.formatData?.color || '#000000';
        color = formColor({ solid: color }, false, 1, undefined, themeData?.colors).backgroundColor;
        linkContent = (
          <span
            className={`entypo ${cookieLink.formatData?.class}`}
            aria-hidden
            style={{
              fontSize: cookieLink.formatData?.size,
              color,
            }}
          />
        );
        break;
      }
      case 'IMAGE':
        linkContent = <Image id={cookieLink.formatData.id} alt="Img" sizes="15vw" className="image" images={images} />;
        break;
      case 'TEXT':
        linkContent = (
          <div style={{ color: linkColor, textDecoration: 'underline' }}>{HTMLParser(cookieLink.name)}</div>
        );
        break;
      default:
        break;
    }

    if (link && ['PAGE', 'SECTION', 'ARTICLE'].includes(cookieLink?.type)) {
      finishedLink = (
        <Link to={link} draggable="false" className={styles.link}>
          {linkContent}
        </Link>
      );
    } else if (link && cookieLink?.type) {
      finishedLink = (
        <a draggable="false" className={styles.link} href={link} target="_blank" rel="noopener noreferrer">
          {linkContent}
        </a>
      );
    }
  }

  return (
    <div id="CookieWarningWrapper" className={styles[`warningBackground${cookie.colour}`]}>
      <div style={{ backgroundColor: bgColor }} className={styles[cookie.layout || 'bottom']}>
        <div className={`container ${styles.container}`} style={{ '--linkColor': linkColor }}>
          <div className={styles.contentWrapper}>
            {cookie.cookieTitle.active && (
              <div className={styles.titleWrapper}>
                <h4 style={{ color: titleColor }}>{HTMLParser(cookie.cookieTitle.text)}</h4>
              </div>
            )}
            <div className={styles.elementWrapper}>
              <div style={{ color: textColor }}>{HTMLParser(cookie?.cookieText?.text ?? cookie.cookieText)}</div>
            </div>
            {['ribbon', 'bottomRight'].includes(cookie.layout) && finishedLink}
          </div>

          <div className={styles.btnWrapper}>
            {!['ribbon', 'bottomRight'].includes(cookie.layout) && finishedLink}
            <div className={styles.cookieButtons}>
              {cookie?.cookieDecline?.active && (
                <button
                  className={clsx(
                    styles.decline,
                    declineButton?.hover?.active && declineButton?.hover?.zoom && styles.zoom,
                    declineButton?.hover?.active && styles.hoverStyle,
                  )}
                  style={
                    declineButtonStyle || {
                      color: btnColor,
                      backgroundColor: btnBgColor,
                      border: `2px solid ${btnBorderColor}`,
                    }
                  }
                  onClick={() => handleClick(false)}
                >
                  {declineIcon}
                  {cookie?.cookieDecline.text}
                </button>
              )}
              {(typeof cookie?.cookieButton === 'string' || cookie?.cookieButton?.active) && (
                <button
                  className={clsx(
                    styles.btn,
                    acceptButton?.hover?.active && acceptButton?.hover?.zoom && styles.zoom,
                    acceptButton?.hover?.active && styles.hoverStyle,
                  )}
                  style={
                    acceptButtonStyle || {
                      color: btnColor,
                      backgroundColor: btnBgColor,
                      border: `2px solid ${btnBorderColor}`,
                    }
                  }
                  onClick={() => handleClick(true)}
                >
                  {acceptIcon}
                  {cookie?.cookieButton?.text ?? cookie.cookieButton}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieWarning;
